import { Activity } from "lucide-react";
import type { ActivityDefinition } from "@/lib/activities";
import {
  getIconComponent,
  ICON_KEYS,
  type IconType,
} from "@/lib/icon-registry";
import { cn } from "@/lib/utils";

const SIZES = {
  sm: { box: "size-7 rounded-full", icon: "size-4" },
  md: { box: "size-10 rounded-xl", icon: "size-[22px]" },
  lg: { box: "size-12 rounded-2xl", icon: "size-[26px]" },
} as const;

const isIconKey = (key: string): key is IconType =>
  (ICON_KEYS as string[]).includes(key);

/**
 * An activity's icon on a soft brand tint. Reads the activity's own `iconKey`
 * (set in admin), so a new or swapped icon shows up here the moment it ships.
 */
export function ActivityGlyph({
  activity,
  size = "md",
  className,
}: {
  activity: Pick<ActivityDefinition, "iconKey">;
  size?: keyof typeof SIZES;
  className?: string;
}) {
  const Icon = isIconKey(activity.iconKey)
    ? getIconComponent(activity.iconKey)
    : null;
  const { box, icon } = SIZES[size];

  return (
    <span
      aria-hidden="true"
      className={cn(
        "flex shrink-0 items-center justify-center bg-brand/10 dark:bg-brand/20",
        box,
        className,
      )}
    >
      {Icon ? (
        // The registry's SVGs paint with `color`, not currentColor.
        <Icon className={icon} color="var(--color-brand)" />
      ) : (
        <Activity className={cn(icon, "text-brand")} />
      )}
    </span>
  );
}
